import { gsap } from 'gsap';

export const inlineBirdhouseAnimation = {
    init(root = document) {
        if (typeof window === 'undefined') return;

        const reduceMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        )?.matches;
        if (reduceMotion) return;

        const sections = Array.from(
            root.querySelectorAll('[data-inline-birdhouse-animation]')
        );
        if (!sections.length) return;

        // Prepare initial state early so users don't see the "finished" SVG
        // before the observer triggers (which causes a blink/jump).
        for (const section of sections) {
            this.prepare(section);
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) continue;

                    const section = entry.target;
                    observer.unobserve(section);
                    this.play(section);
                }
            },
            {
                threshold: 0.25
            }
        );

        for (const section of sections) {
            observer.observe(section);
        }
    },

    prepare(section) {
        if (!(section instanceof HTMLElement)) return;
        if (section.dataset.inlineBirdhouseAnimationPrepared === '1') return;
        if (section.dataset.inlineBirdhouseAnimationPlayed === '1') return;

        const skyline = section.querySelector(
            '[data-inline-birdhouse-animation-skyline]'
        );
        const svg = skyline?.querySelector('svg') ?? null;
        if (!skyline || !svg) return;

        // Match the animation's from-state so there isn't a visible jump when play() starts.
        gsap.set(skyline, {
            scale: 0.97,
            transformOrigin: '50% 100%'
        });

        const houses = Array.from(svg.querySelectorAll('g[id^="house-"]'));
        if (!houses.length) return;

        gsap.set(houses, {
            yPercent: 120,
            transformBox: 'fill-box',
            transformOrigin: '50% 100%'
        });

        section.dataset.inlineBirdhouseAnimationPrepared = '1';
    },

    play(section) {
        if (!(section instanceof HTMLElement)) return;
        if (section.dataset.inlineBirdhouseAnimationPlayed === '1') return;
        section.dataset.inlineBirdhouseAnimationPlayed = '1';

        const skyline = section.querySelector(
            '[data-inline-birdhouse-animation-skyline]'
        );
        const svg = skyline?.querySelector('svg') ?? null;
        if (!skyline || !svg) return;

        // Slow zoom running alongside the house animation.
        gsap.killTweensOf(skyline);
        gsap.fromTo(
            skyline,
            { scale: 0.97 },
            {
                scale: 0.99,
                duration: 7,
                ease: 'none',
                transformOrigin: '50% 100%',
                overwrite: true
            }
        );

        const houses = Array.from(svg.querySelectorAll('g[id^="house-"]'));
        if (!houses.length) return;

        gsap.killTweensOf(houses);

        gsap.set(houses, {
            yPercent: 120,
            transformBox: 'fill-box',
            transformOrigin: '50% 100%'
        });

        const eases = [
            'power1.out',
            'power2.out',
            'power3.out',
            'circ.out',
            'expo.out'
        ];
        const totalDuration = 2.0;
        const maxDelay = 0.55;
        const minDuration = 0.75;

        const tl = gsap.timeline();

        for (const house of houses) {
            const delay = gsap.utils.random(0, maxDelay, 0.01);
            const duration = Math.max(minDuration, totalDuration - delay);
            const ease = eases[Math.floor(gsap.utils.random(0, eases.length))];

            tl.to(
                house,
                {
                    yPercent: 0,
                    duration,
                    ease,
                    overwrite: true
                },
                delay
            );
        }
    }
};

if (typeof window !== 'undefined') {
    window.inlineBirdhouseAnimation = inlineBirdhouseAnimation;
}
